import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { PageLoader } from '@navegasistemas/dls-navega-rjs'
import { ProtectedRoute } from '@/components/protected-route'
import { AdminProtectedRoute } from '@/components/admin-protected-route'
import { GuestRoute } from '@/components/guest-route'
import { AppLayout } from '@/layouts/app-layout'
import { AdminLayout } from '@/layouts/admin-layout'

const LoginPage = lazy(() => import('@/pages/login-page'))
const AdminLoginPage = lazy(() => import('@/pages/admin-login-page'))
const MfaSetupPage = lazy(() => import('@/pages/mfa-setup-page'))
const MfaValidatePage = lazy(() => import('@/pages/mfa-validate-page'))
const ChangePasswordPage = lazy(() => import('@/pages/change-password-page'))
const DashboardPage = lazy(() => import('@/pages/dashboard-page'))
const RolesPage = lazy(() => import('@/pages/roles-page'))
const UsersPage = lazy(() => import('@/pages/users-page'))
const FeatureFlagsPage = lazy(() => import('@/pages/feature-flags-page'))
const ParametersPage = lazy(() => import('@/pages/parameters-page'))
const AiCostsPage = lazy(() => import('@/pages/ai-costs-page'))
const InstitutionsPage = lazy(() => import('@/pages/institutions-page'))
const NavegaStaffPage = lazy(() => import('@/pages/navega-staff-page'))
const StaffRolesPage = lazy(() => import('@/pages/staff-roles-page'))
const LogsPage = lazy(() => import('@/pages/logs-page'))
const AnalyticsPage = lazy(() => import('@/pages/analytics-page'))
const NotificationsPage = lazy(() => import('@/pages/notifications-page'))
const DeviceTokensPage = lazy(() => import('@/pages/device-tokens-page'))
const AppVersionsPage = lazy(() => import('@/pages/app-versions-page'))
const MemberServicePage = lazy(() => import('@/pages/member-service-page'))
const ConversationTraceabilityPage = lazy(() => import('@/pages/conversation-traceability-page'))
const DeployHubPage = lazy(() => import('@/pages/deploy-hub-page'))
const DeployHubClientsPage = lazy(() => import('@/pages/deploy-hub-clients-page'))
const DeployHubClientDetailPage = lazy(() => import('@/pages/deploy-hub-client-detail-page'))
const DeployHubApplicationsPage = lazy(() => import('@/pages/deploy-hub-applications-page'))
const DeployHubReleasesPage = lazy(() => import('@/pages/deploy-hub-releases-page'))
const DeployHubDeploymentsPage = lazy(() => import('@/pages/deploy-hub-deployments-page'))
const DeployHubDeploymentDetailPage = lazy(
  () => import('@/pages/deploy-hub-deployment-detail-page'),
)
const DeployHubApplicationDetailPage = lazy(
  () => import('@/pages/deploy-hub-application-detail-page'),
)
const DeployHubEnvSyncPage = lazy(() => import('@/pages/deploy-hub-env-sync-page'))
const DeployHubEnvSyncDetailPage = lazy(() => import('@/pages/deploy-hub-env-sync-detail-page'))
const DeployHubLogsPage = lazy(() => import('@/pages/deploy-hub-logs-page'))
const PreferencesPage = lazy(() => import('@/pages/preferences-page'))
const M2MClientsPage = lazy(() => import('@/pages/m2m-clients-page'))
const UserDetailPage = lazy(() => import('@/pages/user-detail-page'))
const UserCreatePage = lazy(() => import('@/pages/user-create-page'))
const UserImportPage = lazy(() => import('@/pages/user-import-page'))
const UsersAuditPage = lazy(() => import('@/pages/users-audit-page'))
const RoleDetailPage = lazy(() => import('@/pages/role-detail-page'))
const MyParametersPage = lazy(() => import('@/pages/my-parameters-page'))

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Guest-only routes — redirect to /dashboard when already authenticated */}
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login/mfa/setup" element={<MfaSetupPage />} />
            <Route path="/login/mfa/validate" element={<MfaValidatePage />} />
            <Route path="/login/change-password" element={<ChangePasswordPage />} />
          </Route>

          <Route path="/admin" element={<AdminLoginPage />} />

          {/* Normal user routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/roles" element={<RolesPage />} />
              <Route path="/roles/:id" element={<RoleDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="/users/new" element={<UserCreatePage />} />
              <Route path="/users/import" element={<UserImportPage />} />
              <Route path="/users/audit" element={<UsersAuditPage />} />
              <Route path="/users/:id" element={<UserDetailPage />} />
              <Route path="/preferences" element={<PreferencesPage />} />
              <Route path="/parameters" element={<MyParametersPage />} />
            </Route>
          </Route>

          {/* Admin routes */}
          <Route element={<AdminProtectedRoute />}>
            <Route element={<AdminLayout />}>
              <Route path="/admin/dashboard" element={<DashboardPage />} />
              <Route path="/admin/feature-flags" element={<FeatureFlagsPage />} />
              <Route path="/admin/parameters" element={<ParametersPage />} />
              <Route path="/admin/ai-costs" element={<AiCostsPage />} />
              <Route path="/admin/institutions" element={<InstitutionsPage />} />
              <Route path="/admin/navega-staff" element={<NavegaStaffPage />} />
              <Route path="/admin/staff-roles" element={<StaffRolesPage />} />
              <Route path="/admin/roles" element={<RolesPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/logs" element={<LogsPage />} />
              <Route path="/admin/analytics" element={<AnalyticsPage />} />
              <Route path="/admin/notifications" element={<NotificationsPage />} />
              <Route path="/admin/device-tokens" element={<DeviceTokensPage />} />
              <Route path="/admin/app-versions" element={<AppVersionsPage />} />
              <Route path="/admin/member-service" element={<MemberServicePage />} />
              <Route
                path="/admin/member-service/conversation-traceability"
                element={<ConversationTraceabilityPage />}
              />
              <Route path="/admin/deploy-hub" element={<DeployHubPage />} />
              <Route path="/admin/deploy-hub/clients" element={<DeployHubClientsPage />} />
              <Route path="/admin/deploy-hub/clients/:id" element={<DeployHubClientDetailPage />} />
              <Route
                path="/admin/deploy-hub/applications"
                element={<DeployHubApplicationsPage />}
              />
              <Route
                path="/admin/deploy-hub/applications/:appId"
                element={<DeployHubApplicationDetailPage />}
              />
              <Route
                path="/admin/deploy-hub/applications/:appId/releases"
                element={<DeployHubReleasesPage />}
              />
              <Route path="/admin/deploy-hub/deployments" element={<DeployHubDeploymentsPage />} />
              <Route
                path="/admin/deploy-hub/deployments/:id"
                element={<DeployHubDeploymentDetailPage />}
              />
              <Route path="/admin/deploy-hub/env-sync" element={<DeployHubEnvSyncPage />} />
              <Route
                path="/admin/deploy-hub/env-sync/:id"
                element={<DeployHubEnvSyncDetailPage />}
              />
              <Route path="/admin/deploy-hub/logs" element={<DeployHubLogsPage />} />
              <Route path="/admin/m2m-clients" element={<M2MClientsPage />} />
              <Route path="/admin/preferences" element={<PreferencesPage />} />
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
